import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import useTheme from "../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../providers/ThemeProvider";
import { useAuth } from "../../../../../providers/AuthProvider";
import { GetGraphData } from "../../../../../common/network/Api";
import { BiomarkerItem } from "../../../../../common/entities/type";
import { RootStatePlatform } from "../../../store/platform-slice";
import { chartKeys } from "../../../store/helper";
import { getChartEntries } from "../../healthOverview/biomarkerDisplay";
import {
  buildBiomarkerSeries,
  buildMetricSeries,
  buildUnavailableWearableSeries,
  buildWearableSeries,
  keepsZeroReadings,
  normalizeWearableResponse,
  parseSeriesKey,
  seriesKey,
  TimelineMetricOption,
  TimelineSeries,
  WearableDay,
} from "./timelineSeries";
import { TimelineWindow } from "./timeScale";

/**
 * What the Filters picker offers, and the series for what is selected.
 *
 * Biomarkers and PNOĒ metrics are already in Redux (the `biomarkers` and
 * `report` platform resources); only wearables are fetched here, one
 * `GetGraphData` call per key and period, cached for the session of the tab.
 */

export interface TimelineCatalog {
  metrics: TimelineMetricOption[];
  biomarkers: TimelineMetricOption[];
  wearables: TimelineMetricOption[];
  byKey: Map<string, TimelineMetricOption>;
  /** Biomarkers with the most recent readings first — the default selection. */
  recentBiomarkerKeys: string[];
}

const uniqueBiomarkers = (grouped: Record<number, BiomarkerItem[]>) => {
  const byId = new Map<string, BiomarkerItem>();
  Object.values(grouped ?? {}).forEach((items) =>
    items.forEach((item) => {
      if (!byId.has(item.biomarkerID)) byId.set(item.biomarkerID, item);
    })
  );
  return byId;
};

const latestReading = (item: BiomarkerItem) =>
  Math.max(
    0,
    ...getChartEntries(item).map((e) => new Date(e.dateCreated).getTime() || 0)
  );

export const useTimelineCatalog = () => {
  const biomarkerData = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerData
  );
  const dashboard = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer?.dashboard
  );

  const biomarkersById = useMemo(
    () => uniqueBiomarkers(biomarkerData),
    [biomarkerData]
  );

  const catalog = useMemo<TimelineCatalog>(() => {
    const withReadings = Array.from(biomarkersById.values()).filter(
      // Range-only biomarkers count: their readings plot as hatched bars.
      (item) => getChartEntries(item).length > 0
    );
    const biomarkers = withReadings
      .map((item) => ({
        key: seriesKey("biomarker", item.biomarkerID),
        label: item.name || item.shortName || "",
        unit: item.unit || "",
        group: "biomarker" as const,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const metrics = (dashboard?.metrics ?? [])
      .filter((m) => (m.values ?? []).length > 0)
      .map((m) => ({
        key: seriesKey("metric", m.id),
        label: m.label,
        unit: "%",
        group: "metric" as const,
      }));

    const wearables = chartKeys.map((ck) => ({
      key: seriesKey("wearable", ck.key),
      label: ck.label,
      unit: ck.unit || "",
      group: "wearable" as const,
    }));

    const recentBiomarkerKeys = [...withReadings]
      .sort((a, b) => latestReading(b) - latestReading(a))
      .map((item) => seriesKey("biomarker", item.biomarkerID));

    const byKey = new Map<string, TimelineMetricOption>();
    [...metrics, ...biomarkers, ...wearables].forEach((o) => byKey.set(o.key, o));

    return { metrics, biomarkers, wearables, byKey, recentBiomarkerKeys };
  }, [biomarkersById, dashboard?.metrics]);

  return { catalog, biomarkersById, reportMetrics: dashboard?.metrics ?? [] };
};

// The graphs endpoint takes a week's Monday or a month's first day.
const wearableRequest = (window: TimelineWindow) => ({
  periodType: window.period === "week" ? "weekly" : "monthly",
  date: format(new Date(window.startMs), "yyyy-MM-dd"),
});

const useTimelineSeries = (
  selectedKeys: string[],
  window: TimelineWindow
): { series: TimelineSeries[]; loading: boolean; catalog: TimelineCatalog } => {
  const theme: Theme = useTheme();
  const { AuthUser } = useAuth();
  const { catalog, biomarkersById, reportMetrics } = useTimelineCatalog();

  const cacheRef = useRef<Map<string, WearableDay[]>>(new Map());
  const [wearableDays, setWearableDays] = useState<Record<string, WearableDay[]>>(
    {}
  );
  const [loading, setLoading] = useState(false);

  const { periodType, date } = wearableRequest(window);
  const isYear = window.period === "year";
  // Nothing to fetch in a year: the graphs endpoint has no yearly variant.
  const wearableIds = useMemo(
    () =>
      isYear
        ? []
        : selectedKeys
            .map(parseSeriesKey)
            .filter((k) => k.group === "wearable")
            .map((k) => k.id),
    [selectedKeys, isYear]
  );
  const wearableIdsKey = wearableIds.join(",");

  useEffect(() => {
    let cancelled = false;
    const cacheKey = (id: string) => `${id}|${periodType}|${date}`;
    const missing = wearableIds.filter((id) => !cacheRef.current.has(cacheKey(id)));

    const publish = () => {
      if (cancelled) return;
      const next: Record<string, WearableDay[]> = {};
      wearableIds.forEach((id) => {
        next[id] = cacheRef.current.get(cacheKey(id)) ?? [];
      });
      setWearableDays(next);
    };

    if (missing.length === 0) {
      publish();
      return;
    }
    setLoading(true);
    Promise.all(
      missing.map(async (id) => {
        const info = chartKeys.find((ck) => ck.key === id);
        try {
          const response = await GetGraphData(
            AuthUser?.role,
            periodType,
            date,
            id,
            info?.type ?? ""
          );
          cacheRef.current.set(
            cacheKey(id),
            normalizeWearableResponse(id, response, keepsZeroReadings(info))
          );
        } catch (error) {
          console.error(`Error loading ${id} for the timeline:`, error);
          cacheRef.current.set(cacheKey(id), []);
        }
      })
    ).finally(() => {
      publish();
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // `wearableIdsKey` stands in for the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wearableIdsKey, periodType, date, AuthUser?.role]);

  const series = useMemo(
    () =>
      selectedKeys
        .map((key): TimelineSeries | null => {
          const { group, id } = parseSeriesKey(key);
          if (group === "biomarker") {
            const item = biomarkersById.get(id);
            return item ? buildBiomarkerSeries(item, window, theme) : null;
          }
          if (group === "metric") {
            const metric = reportMetrics.find((m) => m.id === id);
            return metric ? buildMetricSeries(metric, window, theme) : null;
          }
          const option = catalog.byKey.get(key);
          if (!option) return null;
          return isYear
            ? buildUnavailableWearableSeries(option)
            : buildWearableSeries(option, wearableDays[id] ?? [], window, theme);
        })
        .filter((s): s is TimelineSeries => s !== null),
    [selectedKeys, biomarkersById, reportMetrics, catalog, wearableDays, window, theme, isYear]
  );

  return { series, loading, catalog };
};

export default useTimelineSeries;
